import { useCallback, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Box, Button, Typography, TextField, InputLabel } from "@mui/material";
import { IAreaValues, IAreaCreationForm } from "./AreaCreationFormInterfaces";
import { areaFormValidationSchema } from "validations/schemas";
import { useStyles } from "./AreaCreationForm.styles";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import CheckIcon from "@mui/icons-material/Check";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { LanguageCodeMap } from "constants/constants";

export const AreaCreationForm = ({ handleClose, areaToEdit }: IAreaCreationForm) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { machineCenterId } = useParams();
    const { corporations } = useContext(CorporateContext);

    const { apolloMutationError, addOrInsertArea } = FailureDetailsEntity.UpsertArea();
    const { getExistingTranslationId } = FailureDetailsEntity.CheckForExistingTranslationId();
    const { updateTranslatioName, apolloMutationError: errorOnTranslationNameUpdate } =
        FailureDetailsEntity.UpdateTranslationName();
    const { insertTranslatioName, apolloMutationError: errorOnTranslationNameInsertion } =
        FailureDetailsEntity.InsertTranslationName();

    const language = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);

    const languageIdSelected = useMemo(() => LanguageCodeMap[language as keyof typeof LanguageCodeMap], [language]);
    const existingAreaTranslationForLanguage = useMemo(
        () =>
            areaToEdit
                ? areaToEdit.translation.translation_names.find((tn) => tn.language_id === languageIdSelected)
                : undefined,
        [areaToEdit, languageIdSelected],
    );
    const errorMessage = useMemo(() => {
        let message = "";

        if (apolloMutationError?.message.includes("uq_area_name")) {
            message = t("failure_details_setup.add_area_modal.existing_area_error");
        } else if (
            errorOnTranslationNameUpdate?.message.includes("translation_name_name_language_id_corporate_id_key") ||
            errorOnTranslationNameInsertion?.message.includes("translation_name_name_language_id_corporate_id_key")
        ) {
            message = t("failure_details_setup.add_area_modal.existing_area_name_translation_error");
        } else if (apolloMutationError || errorOnTranslationNameUpdate || errorOnTranslationNameInsertion) {
            message = t("failure_details_setup.add_area_modal.generic_error");
        }

        return message;
    }, [apolloMutationError, errorOnTranslationNameUpdate, errorOnTranslationNameInsertion, t]);
    const languageCode = language.toLocaleUpperCase();

    const handleAddAreaSubmit = useCallback(
        async (values: IAreaValues) => {
            const translationNameValues = {
                ...(areaToEdit && { translation_id: areaToEdit.translation.id }),
                language_id: languageIdSelected,
                name: values.name,
                corporate_id: corporations[0].corporate_id,
            };

            if (areaToEdit) {
                try {
                    let response;
                    if (existingAreaTranslationForLanguage) {
                        response = await updateTranslatioName({
                            variables: {
                                translation_id: areaToEdit?.translation.id,
                                language_id: languageIdSelected,
                                changes: { name: values.name },
                            },
                        });
                    } else {
                        response = await insertTranslatioName({
                            variables: {
                                translation: translationNameValues,
                            },
                        });
                    }
                    if (response) {
                        handleClose();
                    }
                } catch {
                    return;
                }
            } else {
                const existingTranslationId = await getExistingTranslationId(translationNameValues);
                const areaValues = {
                    machine_center_id: machineCenterId,
                    ...(existingTranslationId
                        ? { name_translation_id: existingTranslationId }
                        : {
                              translation: {
                                  data: {
                                      translation_names: {
                                          data: translationNameValues,
                                      },
                                  },
                              },
                          }),
                };
                try {
                    const response = await addOrInsertArea({ variables: { area: [areaValues] } });
                    if (response) {
                        handleClose();
                    }
                } catch {
                    return;
                }
            }
        },
        [
            addOrInsertArea,
            areaToEdit,
            handleClose,
            machineCenterId,
            languageIdSelected,
            corporations,
            getExistingTranslationId,
            existingAreaTranslationForLanguage,
            insertTranslatioName,
            updateTranslatioName,
        ],
    );

    const initFormValues: IAreaValues = {
        name: areaToEdit && existingAreaTranslationForLanguage ? existingAreaTranslationForLanguage.name : "",
        machine_center_id: 0,
    };

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={areaFormValidationSchema}
            onSubmit={handleAddAreaSubmit}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-area-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {`${t("failure_details_setup.add_area_modal.name")} (${languageCode})`}
                                </Typography>
                            </InputLabel>
                            <TextField
                                placeholder={
                                    areaToEdit && !existingAreaTranslationForLanguage
                                        ? t("failure_details_setup.missing_translation_placeholder")
                                        : ""
                                }
                                aria-labelledby="outlined-area-name-label"
                                fullWidth
                                id="outlined-area-name-input"
                                className={classes.textField}
                                name="name"
                                type="text"
                                variant="outlined"
                                autoFocus={true}
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                        </Box>
                        <p style={{ display: errorMessage ? "block" : "none", color: "red" }}>{errorMessage}</p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t("failure_details_setup.add_area_modal.cancel")}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {<CheckIcon className={classes.icon} />}{" "}
                                {t("failure_details_setup.add_area_modal.save")}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
